import { DOCUMENT, effect, inject, Service } from '@angular/core';
import { Store } from '@ngxs/store';
import { MessageAlertSquare } from '@digitale-menschen/component-library/untitled-icons';
import { APP_ENVIRONMENT, SupportWidgetConfig } from '@lexplus-environments';
import { ProfileState } from '../states/profile/profile-state';
import { resolvePersonName } from '../functions/person.function';

/**
 * The object the widget's loader reads its configuration from. Written to
 * `window.digitalemenschen_support` before the loader is appended, because the
 * loader reads it while it initialises.
 */
interface SupportWidgetWindowConfig {
  apiKey: string;
  apiBaseUrl: string;
  appName: string;
  position: SupportWidgetConfig['position'];
  launcher: SupportWidgetConfig['launcher'] & { label: string; icon: string };
  title: string;
  subtitle: string;
  labels: Record<string, string>;
  environment: string;
  user?: { id: string; name: string; email?: string };
  metadata?: Record<string, string>;
}

/** What the loaded widget script exposes on `window`. */
interface SupportWidgetApi {
  render: (config: SupportWidgetWindowConfig) => void;
  destroy: () => void;
}

type SupportWidgetWindow = Window & {
  digitalemenschen_support?: SupportWidgetWindowConfig;
  DigitalemenschenSupport?: SupportWidgetApi;
};

/**
 * Runs the Digitale Menschen support widget: the launcher a user reports a
 * problem from, and the report's context.
 *
 * The widget is a third-party script, configured through a global object rather
 * than through arguments — so {@link initialize} fills that object in and only
 * then appends the loader. With the `everyone` audience it is set up **before and
 * independently of a sign-in**, so that a user who cannot get past the sign-in
 * screen can report exactly that; the account is attached later, by the effect
 * below, and is what turns an anonymous report into one support can answer.
 * With the `neosys` audience (production) it shows only while a Neosys user is
 * signed in.
 *
 * Configuration comes from the app's environment ({@link APP_ENVIRONMENT}), so
 * the same service serves both apps and each names itself. An app whose
 * environment carries no `support` block simply doesn't load the widget.
 */
@Service()
export class SupportWidgetService {
  private readonly document = inject(DOCUMENT);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly store = inject(Store);
  private readonly profile = this.store.selectSignal(ProfileState.profile);
  private readonly isNeosysUser = this.store.selectSignal(ProfileState.isAdmin);

  /** Set once the loader script is in the document; later shows go through the widget's API. */
  private loaderAppended = false;

  /** Every text the widget shows, in the «Sie» form and the app's language. */
  private readonly labels: Record<string, string> = {
    panel: $localize`:@@support.labels.panel:Feedback`,
    openPanel: $localize`:@@support.labels.openPanel:Feedback öffnen`,
    closePanel: $localize`:@@common.close:Schliessen`,
    messagePlaceholder: $localize`:@@support.labels.messagePlaceholder:Wie können wir helfen?`,
    dropzoneAction: $localize`:@@common.upload.click:Zum Hochladen klicken`,
    dropzoneHint: $localize`:@@common.upload.dragdrop:oder per Drag & Drop`,
    fileInput: $localize`:@@support.labels.fileInput:Dateien auswählen`,
    removeFile: $localize`:@@common.entfernen:Entfernen`,
    screenshot: $localize`:@@support.labels.screenshot:Screenshot automatisch mitsenden`,
    screenshotDisclaimer: $localize`:@@support.labels.screenshotDisclaimer:Beim Absenden werden Sie gebeten, das zu teilende Fenster auszuwählen. Achten Sie darauf, keine sensiblen oder personenbezogenen Daten zu übermitteln.`,
    submit: $localize`:@@support.labels.submit:Absenden`,
    shortcutHint: $localize`:@@support.labels.shortcutHint:Feedback jederzeit öffnen mit`,
    sending: $localize`:@@support.labels.sending:Wird gesendet …`,
    capturingScreenshot: $localize`:@@support.labels.capturingScreenshot:Screenshot wird erstellt …`,
    messageRequired: $localize`:@@support.labels.messageRequired:Bitte beschreiben Sie Ihr Anliegen.`,
    sendRejected: $localize`:@@support.labels.sendRejected:Senden fehlgeschlagen. Bitte prüfen Sie Ihre Eingaben.`,
    sendFailed: $localize`:@@support.labels.sendFailed:Senden fehlgeschlagen. Bitte versuchen Sie es später erneut.`,
    successTitle: $localize`:@@support.labels.successTitle:Danke!`,
    // `{ticketNumber}` and the other braces are the widget's placeholders, filled in at runtime.
    successMessage: $localize`:@@support.labels.successMessage:Ihr Ticket #{ticketNumber} wurde erstellt. Wir melden uns.`,
    maintenanceTitle: $localize`:@@support.labels.maintenanceTitle:Wartungsarbeiten`,
    maintenanceMessage: $localize`:@@support.labels.maintenanceMessage:Wartungsarbeiten – wir sind in Kürze wieder erreichbar.`,
    tooManyFiles: $localize`:@@support.labels.tooManyFiles:Maximal {maxCount} Dateien – «{fileName}» wurde nicht übernommen.`,
    fileTooLarge: $localize`:@@support.labels.fileTooLarge:«{fileName}» ist {size} gross (maximal {maxSizeMb} MB).`,
    fileTypeRejected: $localize`:@@support.labels.fileTypeRejected:Dateityp von «{fileName}» wird nicht akzeptiert.`,
  };

  /**
   * Keeps the report's sender in step with the signed-in account.
   *
   * An effect and not a one-time read for the same reason the language service
   * needs one: the profile arrives after a sign-in, long after {@link initialize}
   * has run, and it disappears again on sign-out — where the fields are removed
   * rather than left behind, so a report sent from the sign-in screen afterwards
   * does not carry the previous user.
   */
  private readonly signedInUserEffect = effect(() => {
    const config = this.windowConfig;
    if (!config) {
      return;
    }

    const profile = this.profile();
    if (!profile) {
      delete config.user;
      delete config.metadata;
      return;
    }

    config.user = {
      id: profile.id,
      name: resolvePersonName(profile) || (profile.userName ?? ''),
      email: profile.userName,
    };
    if (profile.kundeID === undefined) {
      delete config.metadata;
      return;
    }
    config.metadata = { kundeID: String(profile.kundeID) };
  });

  /** Shows the widget for the `neosys` audience while a Neosys user is signed in, and removes it otherwise. */
  private readonly audienceEffect = effect(() => {
    const isNeosysUser = this.isNeosysUser();
    if (!this.windowConfig || this.environment.support?.audience !== 'neosys') {
      return;
    }
    if (isNeosysUser) {
      this.show();
      return;
    }
    this.windowRef?.DigitalemenschenSupport?.destroy();
  });

  /**
   * Configures the widget and loads it, for the `neosys` audience only once a Neosys
   * user is signed in. Called once per app from an application initializer; a second
   * call does nothing, so a widget already on the page is never configured twice or
   * loaded twice.
   */
  public initialize(): void {
    const support = this.environment.support;
    const windowRef = this.windowRef;
    if (!support || !windowRef || windowRef.digitalemenschen_support) {
      return;
    }

    windowRef.digitalemenschen_support = {
      apiKey: support.apiKey,
      apiBaseUrl: support.apiBaseUrl,
      appName: support.appName,
      position: support.position,
      launcher: { ...support.launcher, label: $localize`:@@support.launcher.label:Feedback`, icon: MessageAlertSquare },
      title: $localize`:@@support.title:Fehler in ${support.appName}:appName: entdeckt?`,
      subtitle: $localize`:@@support.subtitle:Beschreiben Sie Ihr Anliegen, wir melden uns.`,
      labels: this.labels,
      environment: this.environment.name,
    };

    if (this.shouldShow(support)) {
      this.show();
    }
  }

  /** Appends the loader the first time, and re-renders through the widget's API after a removal. */
  private show(): void {
    const support = this.environment.support;
    const windowRef = this.windowRef;
    const config = this.windowConfig;
    if (!support || !windowRef || !config) {
      return;
    }

    if (this.loaderAppended) {
      windowRef.DigitalemenschenSupport?.render(config);
      return;
    }

    // Rounded timestamp: one cache-busting URL per window, so a new widget version
    // is picked up within the hour and every load inside one is served from cache.
    const version = Math.floor(Date.now() / support.cacheWindowMs);
    const loader = this.document.createElement('script');
    loader.src = `${support.loaderUrl}?v=${version}`;
    loader.defer = true;
    // A sign-out while the script was still loading must not leave the widget behind.
    loader.addEventListener('load', () => {
      if (!this.shouldShow(support)) {
        windowRef.DigitalemenschenSupport?.destroy();
      }
    });
    this.document.head.appendChild(loader);
    this.loaderAppended = true;
  }

  private shouldShow(support: SupportWidgetConfig): boolean {
    return support.audience === 'everyone' || this.isNeosysUser();
  }

  private get windowRef(): SupportWidgetWindow | null {
    return this.document.defaultView as SupportWidgetWindow | null;
  }

  private get windowConfig(): SupportWidgetWindowConfig | undefined {
    return this.windowRef?.digitalemenschen_support;
  }
}
